// Appeals: its lists that open by address, and the way from an open list to the page
// where its appeals are decided. No React in here.
//
// The open lists are the dashboard's own: the red-flag (or yellow-flag) appeals still
// waiting, of ANY age, for the people this dashboard is showing. The appeals pages do
// not open on the same rows: they have no Role filter, they list every appeal filed in
// the project (people who have left included), and Red Flags Appeals shows one row per
// employee. So each open list carries a separate, unnumbered button, "Decide in <the
// page's menu name>", offered only to someone who holds that page. The button opens the
// page on its undecided appeals only (`status=open`), as the home screen's appeals cards
// do, so the person lands on what still needs a decision rather than every appeal ever.
//
// "All open appeals" is both open queues as one list, red and yellow together in the
// server's oldest-first order. It is the list the home screen's Appeals number opens, and
// it carries one Decide button for each kind that has an appeal waiting.
import type { MenuItem } from '../../../stores/menuStore';
import { DRILL_ROUTES, DRILL_STATUS_OPEN, buildDrillUrl } from '../../../utils/drilldown';
// The home screen's own date rule, so its row and the yellow page's window cannot drift.
import { readOpenAppeals } from '../../../pages/home/homeNumbers';
import type { DashboardListInfo } from './types';

export type AppealKind = 'red' | 'yellow';

export const APPEALS_LIST = {
  open: 'open',
  raised: 'raised',
  resolved: 'resolved',
  openRed: 'open-red',
  openYellow: 'open-yellow',
} as const;

export const LISTS: DashboardListInfo[] = [
  { slug: APPEALS_LIST.open, name: 'All open appeals', line: 'Every appeal still waiting for a decision, red and yellow, from any month, oldest first.', keywords: 'pending undecided waiting' },
  { slug: APPEALS_LIST.openRed, name: 'Open red-flag appeals', line: 'Every red-flag appeal still waiting for a decision, from any month, oldest first.', keywords: 'pending undecided waiting' },
  { slug: APPEALS_LIST.openYellow, name: 'Open yellow-flag appeals', line: 'Every yellow-flag appeal still waiting for a decision, from any month, oldest first.', keywords: 'pending undecided waiting' },
  { slug: APPEALS_LIST.raised, name: 'Appeals raised', line: 'Every appeal raised in the month, red and yellow.' },
  { slug: APPEALS_LIST.resolved, name: 'Resolved', line: 'Appeals raised in the month that have been decided: overturned or rejected.', keywords: 'decided overturned rejected' },
];

/** An open-list row, as far as the decide button reads it. */
export interface OpenAppealRow {
  id?: number | string;
  status?: string;
  createdAt: string | null;
}

/** A button to the page where one kind of appeal is decided. */
export interface DecideLink {
  kind: AppealKind;
  label: string;
  to: string;
}

// Yellow Flag Appeals opens narrowed to flags dated THIS month, which hides an older
// appeal that is still waiting. Opened on the days these appeals were raised, all of them
// show: the same window the home screen's yellow row uses. No window when a row carries
// no readable date, or when the home screen's reader would pass a row over (it could then
// lie outside the window). Either way only the undecided appeals are listed.
function yellowPageAddress(rows: OpenAppealRow[], today: string): string {
  const open = readOpenAppeals([rows]);
  const from = open && open.count === rows.length ? open.oldestRaisedOn : null;
  return buildDrillUrl(
    DRILL_ROUTES.yellowAppeals,
    from ? { startDate: from, endDate: today, status: DRILL_STATUS_OPEN } : { status: DRILL_STATUS_OPEN },
  );
}

/**
 * The button to the page where this kind is decided; null when the person does not hold
 * the page. `rows` are this kind's open rows on the dashboard and `today` is India's
 * date: together they are the window the yellow page is opened on.
 */
export function decideLink(kind: AppealKind, page: MenuItem | null, rows: OpenAppealRow[], today: string): DecideLink | null {
  if (!page) return null;
  return {
    kind,
    label: `Decide in ${page.name}`,
    to: kind === 'yellow' ? yellowPageAddress(rows, today) : buildDrillUrl(DRILL_ROUTES.appeals, { status: DRILL_STATUS_OPEN }),
  };
}
